import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../lib/api';
import { useBranding } from './BrandingContext';

/**
 * Auth state.
 *
 * SECURITY:
 * The JWT lives in an httpOnly cookie set by the backend at /api/auth/login.
 * It is NEVER read by JS and NEVER stored in localStorage — that mitigates XSS
 * token-theft, the original concern flagged in the security audit.
 *
 * We still cache the non-sensitive `user` profile object in localStorage so the
 * UI can hydrate instantly on reload without a /auth/me round-trip. On mount we
 * confirm the cookie is still valid by calling /auth/me; if that returns 401 we
 * drop the cached user.
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setCompanyContext } = useBranding();
  // Guards the /auth/me race: if login()/signup() already succeeded during
  // this mount cycle, initAuth's 401 from a not-yet-authenticated /auth/me
  // must NOT overwrite that fresh user state.
  const localAuthSetRef = useRef(false);

  useEffect(() => {
    const initAuth = async () => {
      const savedUser = localStorage.getItem('user');
      const savedCompany = localStorage.getItem('company_branding');

      // Optimistically hydrate from cache so the UI doesn't flash
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (_e) {
          localStorage.removeItem('user');
        }
        if (savedCompany) {
          try {
            setCompanyContext(JSON.parse(savedCompany));
          } catch (_e) {
            localStorage.removeItem('company_branding');
          }
        }
      }

      // Verify session via the httpOnly cookie
      try {
        const response = await authAPI.getMe();
        // Only accept /me result if a local login hasn't taken over meanwhile.
        if (!localAuthSetRef.current) {
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        }
      } catch (_err) {
        // No valid session — but ONLY reset state if we haven't just logged in
        // during this mount (would clobber a demo auto-login).
        if (!localAuthSetRef.current) {
          localStorage.removeItem('user');
          localStorage.removeItem('company_branding');
          setUser(null);
          setCompanyContext(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [setCompanyContext]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      const { user: userData, company } = response.data;

      // Cookie is already set by the backend; only cache the profile in JS.
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      localAuthSetRef.current = true;

      if (company) {
        localStorage.setItem('company_branding', JSON.stringify(company));
        setCompanyContext(company);
      } else {
        localStorage.removeItem('company_branding');
        setCompanyContext(null);
      }

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const signup = async (email, password, name, companyName, role = 'admin') => {
    setError(null);
    try {
      const response = await authAPI.signup({ email, password, name, company_name: companyName, role });
      const { user: userData } = response.data;

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      localAuthSetRef.current = true;

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Signup failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (_e) {
      // even if the network call fails, clear local state below
    }
    localStorage.removeItem('user');
    localStorage.removeItem('company_branding');
    setUser(null);
    setCompanyContext(null);
    localAuthSetRef.current = false;
  }, [setCompanyContext]);

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    signup,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
